import { Box, Container, Grid } from "@mui/material";
import { useParams, useSearchParams } from "react-router-dom";
import SuppliesDetailHeader from "./SuppliesDetailHeader/SuppliesDetailHeader";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FormattedOwnerData } from "../../core/models/ownerData/ownerData.model";
import { getSuppliesDetail as getSuppliesDetailFetch } from "../../core/services/suppliesDetail/suppliesDetail.service";
import {
  GetSuppliesDetailRequestBody,
  SuppliesDetailCardData,
  SuppliesDetailObject,
} from "../../core/models/suppliesDetail/suppliesDetail.model";
import {
  formatCupsDetail,
  formatSuppliesDirectionHolder,
  initialSuppliesDetail,
} from "../../utils/components/suppliesDetail/suppliesDetail.util";
import SuppliesDetailCard from "./SuppliesDetailCard/SuppliesDetailCard";
import SuppliesBillsStateContent from "../SuppliesBillsStateContent/SuppliesBillsStateContent";
import LastBills from "../LastBills/LastBills";
import TotalEnergiesContext from "../../core/store/context/TotalEnergiesContext";
import SuppliesDetailHistoric from "./SuppliesDetailHistoric/SuppliesDetailHistoric";
import {
  FormattedManagementsList,
  GetManagementsRequestBody,
  ManagementCodeText,
} from "../../core/models/managements/managements.model";
import { formatDateString } from "../../utils/dateUtils/formatDate.util";
import { getManagements } from "../../core/services/managements/managements.service";
import { formatManagementsList } from "../../utils/components/managements/managements.util";
import {
  getBillSelectedFromLocalStorage,
  getCifCupsFromLocalStorage,
  getSupplySelectedFromLocalStorage,
} from "../../core/services/localStorage/localStorage.service";
import { getSupplyOrBillGeneralData } from "../../utils/components/bills/bills.util";
import { isNil } from "lodash";
import { RECIPIENT_IDS_LABEL } from "../../core/constants/constants";
import { AGENT_MANAGEMENT_SERVICE_CONSTANTS } from "../../core/constants/agentManagements.constants";

const SuppliesDetail = () => {
  const { idCups } = useParams();
  const [searchParms] = useSearchParams();
  const { ownerData, userData, isAgent } = useContext(TotalEnergiesContext);

  const [suppliesDetail, setSuppliesDetail] = useState<SuppliesDetailObject>(
    initialSuppliesDetail,
  );
  const [historicUpdates, setHistoricUpdates] = useState<
    FormattedManagementsList[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isHistoricLoading, setIsHistoricLoading] = useState<boolean>(false);
  const selectedSupply = getSupplySelectedFromLocalStorage();
  const selectedBillRow = getBillSelectedFromLocalStorage();
  const cifCups = getCifCupsFromLocalStorage();

  const isBillsOrigin =
    searchParms.get("origin") === "bills" && !selectedSupply;

  const supplyGeneralData = useMemo(() => {
    if (isNil(selectedBillRow) && isNil(selectedSupply)) {
      return null;
    }
    return getSupplyOrBillGeneralData({
      selectedBillRow: isBillsOrigin ? selectedBillRow : null,
      selectedSupply: !isBillsOrigin ? selectedSupply : null,
    });
  }, [isBillsOrigin, selectedBillRow, selectedSupply]);

  const activeContract = !isBillsOrigin ? selectedSupply?.mainContract : null;

  const getSuppliesDetail = useCallback(
    async (
      { isAdministrator, idClient, idAdministrator }: FormattedOwnerData,
      idCups: string,
      token: string,
      contractId: string,
      abortSignal: AbortSignal,
    ) => {
      const getSuppliesRequestBody: GetSuppliesDetailRequestBody = {
        userid: isAdministrator ? idAdministrator : idClient,
        isAdmin: isAdministrator.toString(),
        cupsContractId: contractId,
        cupsId: idCups,
      };

      await getSuppliesDetailFetch(getSuppliesRequestBody, token, abortSignal)
        .then((response) => {
          setSuppliesDetail(formatCupsDetail(response));
          setIsLoading(false);
        })
        .catch((error) => {
          setIsLoading(false);
        });
    },
    [setIsLoading],
  );

  const getHistoricUpdates = useCallback(
    (
      token: string,
      cif: string[],
      cupCode: string,
      contractId: string,
      abortSignal: AbortSignal,
    ) => {
      const dateTo = new Date(new Date().setDate(new Date().getDate() + 1));
      const dateFrom = new Date(new Date(1, 1, 2000));

      const getManagementsRequestBody: GetManagementsRequestBody = {
        Ids: cif,
        IdTipoRegistro: [1, 2, 3, 4, 7, 8],
        fechaDesde: formatDateString(dateFrom).replace(",", ""),
        fechaHasta: formatDateString(dateTo).replace(",", ""),
        pageSize: "100",
        pageIndex: "0",
        IdSubTipoReclamacion: [],
      };

      getManagements(getManagementsRequestBody, token, abortSignal)
        .then(({ CallCenters: result }) => {
          const resultFiltered = result.filter(({ CodigoCups, IdContrato }) => {
            return (
              CodigoCups === cupCode && IdContrato.toString() === contractId
            );
          });
          setIsHistoricLoading(false);
          setHistoricUpdates(formatManagementsList(resultFiltered));
        })
        .catch(() => {
          setIsHistoricLoading(false);
        });
    },
    [],
  );

  useEffect(() => {
    const suppliesDetailsAbortController = new AbortController();
    const managementsAbortController = new AbortController();
    const cupsId = cifCups?.listCupsCifRaw
      .flatMap((item) => item.cups)
      .find((cup) => cup.cupsCode === supplyGeneralData?.cups)?.cupsId;

    if (ownerData && userData) {
      setIsLoading(true);
      setIsHistoricLoading(true);
      getSuppliesDetail(
        ownerData,
        cupsId || idCups!,
        userData.token,
        supplyGeneralData?.contractId
          ? supplyGeneralData?.contractId.toString()
          : "",
        suppliesDetailsAbortController.signal,
      );
      getHistoricUpdates(
        userData.token,
        !isBillsOrigin && activeContract
          ? [activeContract.Identidad]
          : supplyGeneralData?.cif || [],
        supplyGeneralData?.cups || "",
        supplyGeneralData?.contractId
          ? supplyGeneralData?.contractId.toString()
          : "",
        managementsAbortController.signal,
      );

      return () => {
        suppliesDetailsAbortController.abort();
        managementsAbortController.abort();
      };
    }
  }, [getHistoricUpdates, getSuppliesDetail, ownerData, userData]);

  const holderDirectionCardData: SuppliesDetailCardData[] = [
    {
      id: "holder",
      title: "Titular",
      titleIcon: "t-icon-building",
      height: "25rem",
      data: [
        {
          id: "cif",
          label: "CIF",
          value: isBillsOrigin
            ? supplyGeneralData?.cif?.[0] || ""
            : activeContract?.Identidad || "",
        },
        {
          id: "socialReason",
          label: "Tablas.Razón social",
          value: suppliesDetail.socialReason,
        },
        {
          id: "fiscalAddress",
          label: "Dirección fiscal",
          value:
            suppliesDetail.holderAddress === "-"
              ? "-"
              : formatSuppliesDirectionHolder(suppliesDetail),
        },
        {
          id: "zipCode",
          label: "Código postal",
          value: suppliesDetail.holderZipCode,
        },
        {
          id: "contractSituation",
          label: "Situación del contrato",
          value: suppliesDetail.contractSituation,
          renderContent: (value: string) => (
            <SuppliesBillsStateContent state={value} />
          ),
        },
      ],
    },
    {
      id: "suppliesDirection",
      title: "Dirección de suministro",
      titleIcon: "t-icon-map",
      height: "25rem",
      data: [
        { id: "address", label: "Dirección", value: suppliesDetail.address },
        {
          id: "addressNumber",
          label: "Número",
          value: suppliesDetail.addressNumber,
        },
        {
          id: "town",
          label: "Ciudad",
          value: suppliesDetail.town,
        },
        {
          id: "zipCode",
          label: "Código postal",
          value: suppliesDetail.zipCode,
        },
        {
          id: "clarifier",
          label: "Aclarador",
          value: suppliesDetail.clarifier,
        },
      ],
    },
  ];

  const processContactCardData = () => {
    const emails = suppliesDetail.contact.emails.map((email, index) => ({
      id: `e-${email}-${index.toString()}`,
      label: index === 0 ? "Correo principal" : "Correo " + (index + 1),
      value: email,
    }));

    const phones = suppliesDetail.contact.phones.map((phone, index) => ({
      id: `p-${phone}-${index.toString()}`,
      label: index === 0 ? "Teléfono principal" : "Teléfono " + (index + 1),
      value: phone,
    }));

    return [...emails, ...phones];
  };

  const paymentAddressContactCardData: SuppliesDetailCardData[] = [
    {
      id: "paymentMethod",
      title: "Método de pago",
      height: "45rem",
      navigateTo: `${process.env
        .REACT_APP_MANAGEMENT_UPDATE_BANK_DOMICILIATION!}?idCups=${idCups}`,
      data: [
        {
          id: "name",
          label: "Nombre",
          value: suppliesDetail.paymentMethod.name,
        },
        {
          id: "collective",
          label: "Colectivo",
          value: suppliesDetail.paymentMethod.collective,
        },
        {
          id: "cif",
          label: "Tablas.CIF",
          value: suppliesDetail.paymentMethod.cif,
        },
        {
          id: "typeOfPayment",
          label: "Tipo de cobro",
          value: suppliesDetail.paymentMethod.typeOfPayment,
        },
        {
          id: "iban",
          label: "IBAN",
          value: suppliesDetail.paymentMethod.iban,
        },
      ],
    },
    {
      id: "deliveryAddress",
      title: "Dir. de correspondencia",
      height: "45rem",
      navigateTo: `${process.env
        .REACT_APP_MANAGEMENT_UPDATE_CORRESPONDENCE_ADDRESS!}?idCups=${idCups}`,
      data: [
        {
          id: "data",
          label: "Datos",
          value: suppliesDetail.deliveryAddress.data,
        },
        {
          id: "town",
          label: "Población",
          value: suppliesDetail.deliveryAddress.town,
        },
        {
          id: "zipCode",
          label: "Código postal",
          value: suppliesDetail.deliveryAddress.zipCode,
        },
        {
          id: "address",
          label: "Dirección",
          value: suppliesDetail.deliveryAddress.address,
        },
        {
          id: "addressNumber",
          label: "Número",
          value: suppliesDetail.deliveryAddress.addressNumber,
        },
        {
          id: "clarifier",
          label: "Aclarador",
          value: suppliesDetail.deliveryAddress.clarifier,
        },
      ],
    },
    {
      id: "contact",
      title: "Header.Perfil.Contacto",
      height: "45rem",
      navigateTo: `${process.env
        .REACT_APP_MANAGEMENT_UPDATE_CONTACT!}?idCups=${idCups}`,
      data: processContactCardData(),
    },
  ];

  return (
    <Box component="section" className="supplies-detail">
      <SuppliesDetailHeader
        suppliesDetail={suppliesDetail}
        isLoading={isLoading}
      />
      <Container>
        <Box
          paddingBottom={{ xs: "1.5rem", md: "5rem" }}
          paddingTop={{ xs: "1.5rem", md: "5rem" }}
        >
          <Box component="article">
            <Grid container spacing={2}>
              {holderDirectionCardData.map((cardData) => (
                <Grid item xs={12} md={6} key={cardData.id}>
                  <SuppliesDetailCard
                    cardData={cardData}
                    isLoading={isLoading}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
          <LastBills
            seeAllButtonText="Facturas de este suministro"
            idCups={idCups}
            cups={supplyGeneralData?.cups}
            cif={activeContract?.Identidad ?? supplyGeneralData?.cif?.[0] ?? ""}
            socialReason={
              activeContract?.NombreCliente ??
              supplyGeneralData?.socialReason?.[0]
            }
            cupsContractId={supplyGeneralData?.contractId || 0}
          />
          <Box component="article">
            <Grid container spacing={2}>
              {paymentAddressContactCardData.map((cardData) => (
                <Grid item xs={12} md={4} key={cardData.id}>
                  <SuppliesDetailCard
                    cardData={cardData}
                    isLoading={isLoading}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
          <Box component="article">
            <SuppliesDetailHistoric
              codeCups={suppliesDetail.cupsCode}
              historicUpdates={historicUpdates}
              isLoading={isHistoricLoading}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default SuppliesDetail;
